import React from 'react';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Text } from '@newcore/ui/typography';
import { FavoriteButton } from '@/components/ui/buttons/favorite-button';
import type { UserApplication } from '@/types/api.types';
import { AppIconBox } from './app-icon-box';

interface ApplicationTileProps {
  application: UserApplication;
  onClick: (application: UserApplication) => void;
  isLoading?: boolean;
}

export const ApplicationTile: React.FC<ApplicationTileProps> = React.memo(
  ({ application, onClick, isLoading = false }) => {
    const isFavorite = application.is_favorite;

    const handleClick = () => {
      if (!isLoading) {
        onClick(application);
      }
    };

    return (
      <div className='group relative flex w-16 shrink-0 flex-col items-center gap-1.5'>
        <button
          type='button'
          onClick={handleClick}
          disabled={isLoading}
          title={application.app_name}
          data-testid={`application-tile-${application.app_id}`}
          className='flex flex-col items-center gap-1.5'
        >
          <AppIconBox
            application={application}
            size={Sizes.LG}
            className='group-hover:border-primary group-hover:shadow-xs'
          />

          <Text
            size={TextSizes.XS}
            weight={FontWeights.MEDIUM}
            className={cn(
              'line-clamp-2 w-24 break-words text-center transition-colors',
              isLoading
                ? 'text-tertiary'
                : 'text-quaternary group-hover:text-secondary'
            )}
          >
            {application.app_name}
          </Text>
        </button>

        <div
          className={cn(
            'absolute -right-1.5 -top-1.5 transition-opacity',
            !isFavorite &&
              'md:invisible md:group-hover:visible md:group-focus-within:visible'
          )}
        >
          <FavoriteButton
            applicationId={application.app_id}
            isFavorite={isFavorite}
            size={Sizes.SM}
          />
        </div>
      </div>
    );
  }
);

ApplicationTile.displayName = 'ApplicationTile';
